import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getToken } from '../api/client.js';

/**
 * One live connection for the whole application.
 *
 * The bell and the chat both need the server's push, and opening an
 * EventSource per component would burn two of the browser's six connections
 * per host for the same data. So a single stream lives here and components
 * subscribe to the event they care about.
 *
 * Reconnection is the browser's job: EventSource retries on its own, and the
 * server sends a `retry:` hint plus a heartbeat, so a dropped Wi-Fi comes back
 * without a line of code here.
 */
const RealtimeContext = createContext({ subscribe: () => () => {}, connected: false });

export const RealtimeProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  // event name -> Set of handlers
  const handlers = useRef(new Map());

  useEffect(() => {
    const token = getToken();
    if (!token) return undefined;

    const source = new EventSource(`/api/chat/stream?token=${encodeURIComponent(token)}`);
    source.onopen = () => setConnected(true);
    source.onerror = () => setConnected(false);

    const dispatch = (name) => (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      for (const handler of handlers.current.get(name) ?? []) handler(payload);
    };

    // Named events, so a listener never has to filter someone else's traffic.
    const names = ['message', 'notification'];
    const listeners = names.map((name) => {
      const fn = dispatch(name);
      source.addEventListener(name, fn);
      return [name, fn];
    });

    return () => {
      for (const [name, fn] of listeners) source.removeEventListener(name, fn);
      source.close();
      setConnected(false);
    };
  }, []);

  const value = useMemo(
    () => ({
      connected,
      /** Returns the unsubscribe function, so an effect can just return it. */
      subscribe: (name, handler) => {
        if (!handlers.current.has(name)) handlers.current.set(name, new Set());
        handlers.current.get(name).add(handler);
        return () => handlers.current.get(name)?.delete(handler);
      },
    }),
    [connected],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};

export const useRealtime = () => useContext(RealtimeContext);

/** Sugar for the common case: run `handler` on every `name` event. */
export const useRealtimeEvent = (name, handler) => {
  const { subscribe } = useRealtime();
  const saved = useRef(handler);
  saved.current = handler;

  useEffect(() => subscribe(name, (payload) => saved.current(payload)), [name, subscribe]);
};
